import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import QRCode from 'qrcode';
import { Router } from '@angular/router';
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-deposit-confirmation',
  standalone: true,
  imports: [CommonModule, FormsModule, TextInputComponent, ButtonComponent],
  templateUrl: './deposit-confirmation.component.html',
  styleUrls: ['./deposit-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepositConfirmationComponent implements OnInit {
  private router = inject(Router);

  @Input() title = 'Deposit'; // Customizable title
  @Input() entityName = 'Entity Name'; // Generic entity name (merchant, system, etc.)
  @Input() entityLabel = 'Entity'; // Label for the entity field
  @Input() walletAddress = '0xB95A455B791CfB996EB085D467DD6E8e579F9Bb0'; // Wallet address
  @Input() qrCodeData = ''; // Data for the QR code, if generated dynamically
  @Input() actionButtonText = 'New Deposit'; // Customizable button text
  @Input() isSettlementApp = false;
  @Input() amount?: string = '';
  @Input() currency?: string = '';
  @Input() userType? = '';
  @Input() identifier? = '';
  @Input() blockchainLabel? = '';

  @Output() copyAddressClicked = new EventEmitter<string>();
  @Output() actionButtonClicked = new EventEmitter<void>();

  @ViewChild('qrCanvas') qrCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('pdfCloneContainer', { static: false })
  pdfCloneContainer!: ElementRef<HTMLDivElement>;

  isDashboard = false;

  qrCodeImageSrc = '';

  ngOnInit(): void {
    // If qrCodeData is empty, use walletAddress as the QR code data
    if (!this.qrCodeData) {
      this.qrCodeData = this.walletAddress;
    }

    this.isDashboard = this.router.url.startsWith('/dashboard');

    // Generate QR code using the qrcode library
    this.generateQRCode();
  }

  private async generateQRCode(): Promise<void> {
    try {
      // Generate QR code as data URL
      this.qrCodeImageSrc = await QRCode.toDataURL(this.qrCodeData, {
        width: 195,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      // Fallback to empty string if QR code generation fails
      this.qrCodeImageSrc = '';
    }
  }

  onShareClick(): void {
    this.exportPdfFromHiddenClone();
  }

  private async exportPdfFromHiddenClone(): Promise<void> {
    const cloneEl = this.pdfCloneContainer.nativeElement;
    const canvas = await html2canvas(cloneEl, { scale: 2 });
    const imgData = canvas.toDataURL('image/jpeg', 0.7);
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = (canvas.height * pageWidth) / canvas.width;

    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
    const timestamp = new Date()
      .toISOString()
      .replace(/[:-]/g, '')
      .replace(/\.\d+Z$/, '');
    const fileName = `deposit_slip_${this.walletAddress}_${timestamp}.pdf`;
    pdf.save(fileName);
  }

  onNewDepositClick(): void {
    this.actionButtonClicked.emit();
  }
  get label() {
    return this.isSettlementApp && this.isDashboard ? 'System' : this.entityLabel || 'Entity';
  }

  get name() {
    return this.isSettlementApp && this.isDashboard ? 'Olivepay' : this.entityName;
  }
}
